// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React from "react";
import { useTranslation } from "react-i18next";
import { Col, Form, Row, Select, Upload } from "antd";
import FormField from "../../../components/FormField";
import { FIELD_LENGTH } from "../../../../utils/Constant";
import { BroadcastListInfoResponseDTO } from "../../../../api/backend-openapi";
import { BroadcastListFormProps } from "../../../types/BroadcastListTypes";
import { FormInstance } from "antd/lib/form";
import logger from "../../../../utils/logger";
import { useStore } from "effector-react";
import { BranchesStore } from "../../../../state/branches/BranchesStore";

interface Props extends BroadcastListFormProps {
  broadcastList?: BroadcastListInfoResponseDTO | null;
  showRequired?: boolean;
  form: FormInstance;
  emailError?: JSX.Element;
}

const BroadcastListSettingsFormSection: React.FunctionComponent<Props> = (
  props: Props
) => {
  const { t } = useTranslation();
  const { broadcastList, editMode, showRequired, form, emailError } = props;

  const branchList = useStore(BranchesStore);

  return (
    <>
      <Row>
        <Col xs={24}>
          <FormField
            labelName={t("broadcast_list.name")}
            inputId="name"
            required={showRequired}
            emptyErrorMessage={t("global.empty_error_field")}
            readOnly={!editMode}
            maxLength={FIELD_LENGTH.NAME}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={24} sm={12}>
          <Form.Item
            name="branch"
            key="branch"
            label={<span>{t("broadcast_list.branch.select")}</span>}
          >
            <Select
              id="branch"
              placeholder={t("broadcast_list.branch.placeholder")}
              disabled={!editMode}
              allowClear
            >
              {Object.keys(branchList).map(key => (
                <Select.Option key={key} value={key}>
                  {branchList[key].label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col xs={24}>
          {emailError}
          <Form.Item
            name="emails"
            key="emails"
            rules={[
              {
                required: showRequired,
                message: t("global.empty_error_field")
              }
            ]}
            label={<span>{t("broadcast_list.emails.label")}</span>}
          >
            <Select
              mode="tags"
              options={[]}
              open={false}
              disabled={!editMode}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col xs={24}>
          {editMode && (
            <Upload
              accept=".txt, .csv"
              showUploadList={false}
              className="avatar-uploader"
              listType="picture-card"
              beforeUpload={file => {
                const reader = new FileReader();

                reader.onload = e => {
                  if (e !== null && e.target !== null) {
                    const fileContent = e.target.result?.toString();
                    if (fileContent) {
                      form.setFields([
                        {
                          name: "emails",
                          value: [...new Set(fileContent.split(/\s+/))]
                        }
                      ]);
                    } else {
                      logger.error("Did not retrieve file content");
                    }
                  } else {
                    logger.error("Invalid file upload error");
                  }
                };
                reader.readAsText(file);

                // Prevent upload
                return false;
              }}
            >
              <div className="ant-upload-button">
                <div className="ant-upload-text">
                  {t("broadcast_list.emails.import")}
                </div>
              </div>
            </Upload>
          )}
        </Col>
      </Row>
      {broadcastList && (
        <Row>
          <Col xs={24} md={12}>
            <FormField labelName="Id" inputId="id" readOnly={true} />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <FormField
              labelName={t("broadcast_list.creation_date")}
              inputId="creationDate"
              readOnly={true}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <FormField
              labelName={t("broadcast_list.modification_date")}
              inputId="modificationDate"
              readOnly={true}
            />
          </Col>
        </Row>
      )}
    </>
  );
};

export default BroadcastListSettingsFormSection;
